import { ComponentRef, Directive, ElementRef, inject, input, NgZone, OnDestroy, signal, TemplateRef } from '@angular/core';
import { Overlay, OverlayRef, OverlayPositionBuilder, ConnectedPosition } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { TooltipComponent } from './tooltip.component';

/**
 * Tooltip position relative to the trigger element.
 */
export type TooltipPosition = 'above' | 'below' | 'left' | 'right';

/** Duration of a long-press touch before the tooltip appears (ms). */
const TOUCH_LONG_PRESS_MS = 500;

/** Auto-dismiss delay after a touch-triggered tooltip is shown (ms). */
const TOUCH_AUTO_DISMISS_MS = 3000;

/**
 * Directive that attaches a tooltip to an element.
 * Uses CDK Overlay to render the tooltip outside the DOM hierarchy,
 * avoiding stacking context issues with sticky headers and grid cells.
 *
 * **Mouse users:** tooltip appears on `mouseenter` and hides on `mouseleave`.
 * **Touch users:** a long-press (~500 ms) shows the tooltip; it auto-dismisses
 * after 3 s or when the user taps anywhere outside.
 *
 * @example
 * ```html
 * <button zagTooltip="Click to save">Save</button>
 * <span [zagTooltip]="dynamicText" tooltipPosition="below">Hover me</span>
 * ```
 */
@Directive({
  selector: '[zagTooltip]',
  host: {
    '(mouseenter)': 'show()',
    '(mouseleave)': 'hide()',
    '(focus)': 'hideImmediately()',
    '(blur)': 'hide()',
    '(click)': 'onHostClick($event)',
    '(touchstart)': 'onTouchStart()',
    '(touchmove)': 'onTouchMove()',
    '(touchend)': 'onTouchEnd()',
    '(touchcancel)': 'onTouchCancel()',
  },
})
export class TooltipDirective implements OnDestroy {
  private readonly overlay = inject(Overlay);
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly positionBuilder = inject(OverlayPositionBuilder);
  private readonly zone = inject(NgZone);

  /**
   * The tooltip text or template to display.
   * Optional so the directive can also be applied via `hostDirectives` from a
   * component that provides content programmatically via {@link setContent}.
   */
  readonly zagTooltip = input<string | TemplateRef<unknown> | null>(null);

  /**
   * Position of the tooltip relative to the trigger element.
   * @default 'above'
   */
  readonly tooltipPosition = input<TooltipPosition>('above');

  /**
   * Whether the tooltip is disabled.
   */
  readonly tooltipDisabled = input<boolean>(false);

  /**
   * Programmatic content set via {@link setContent}. Takes precedence over the
   * `zagTooltip` input — lets a host component (using `hostDirectives`) feed a
   * `TemplateRef` it created in its own view, which cannot be wired via input
   * forwarding.
   */
  private readonly programmaticContent = signal<string | TemplateRef<unknown> | null>(null);

  private overlayRef: OverlayRef | null = null;
  private tooltipRef: ComponentRef<TooltipComponent> | null = null;
  private showTimeout: ReturnType<typeof setTimeout> | null = null;
  private hideTimeout: ReturnType<typeof setTimeout> | null = null;

  /** Listeners registered on the overlay panel to bridge hover from trigger → tooltip. */
  private overlayMouseEnterListener: ((event: Event) => void) | null = null;
  private overlayMouseLeaveListener: ((event: Event) => void) | null = null;

  /** Timeout for long-press detection on touch devices. */
  private longPressTimeout: ReturnType<typeof setTimeout> | null = null;

  /** Auto-dismiss timeout after a touch-triggered tooltip is shown. */
  private touchDismissTimeout: ReturnType<typeof setTimeout> | null = null;

  /** Whether the tooltip was triggered by a touch interaction. */
  private touchTriggered = false;

  private touchMoved = false;

  /** Whether the pointer is currently over the overlay panel. Lets owners defer dismissal while the panel is hovered. */
  readonly panelHovered = signal(false);

  /** Listener to dismiss tooltip on outside tap (registered on `document`). */
  private outsideTapListener: ((event: Event) => void) | null = null;

  ngOnDestroy(): void {
    this.hideImmediately();
    this.clearLongPress();
    this.removeOutsideTapListener();
  }

  /**
   * Set tooltip content from the host component. Use when the directive is
   * attached via `hostDirectives` and the content is a `TemplateRef` created
   * inside the component's own view (which cannot be passed via input forwarding).
   * Pass `null` to clear.
   */
  setContent(content: string | TemplateRef<unknown> | null): void {
    this.programmaticContent.set(content);

    // Refresh an already-attached overlay so the new content is rendered
    // without the user having to re-hover.
    if (this.overlayRef) {
      this.applyContentToOverlay();
    }
  }

  /**
   * Show the tooltip (mouse / programmatic).
   */
  show(): void {
    if (this.tooltipDisabled() || !this.resolveContent()) {
      return;
    }

    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
      this.hideTimeout = null;
    }

    if (this.showTimeout) {
      return;
    }

    this.showTimeout = setTimeout(() => {
      this.showTimeout = null;
      if (!this.elementRef.nativeElement.isConnected) {
        return;
      }
      if (!this.resolveContent()) {
        return;
      }
      this.createOverlay();
    }, 100);
  }

  /**
   * Hide the tooltip with a short debounce (mouse / programmatic).
   */
  hide(): void {
    if (this.showTimeout) {
      clearTimeout(this.showTimeout);
      this.showTimeout = null;
    }

    if (this.hideTimeout) {
      return;
    }

    this.hideTimeout = setTimeout(() => {
      this.hideTimeout = null;
      this.destroyOverlay();
    }, 50);
  }

  /**
   * Hide the tooltip without any delay.
   */
  hideImmediately(): void {
    if (this.showTimeout) {
      clearTimeout(this.showTimeout);
      this.showTimeout = null;
    }

    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
      this.hideTimeout = null;
    }

    this.clearTouchDismissTimeout();
    this.removeOutsideTapListener();
    this.touchTriggered = false;
    this.destroyOverlay();
  }

  /**
   * Handle `click` on the host element.
   * When the tooltip was opened by a touch long-press, the subsequent
   * synthetic click must NOT close it; otherwise hide immediately.
   */
  onHostClick(event: Event): void {
    if (this.touchTriggered) {
      event.preventDefault();
      return;
    }
    this.hideImmediately();
  }

  /**
   * Handle `touchstart` — begin long-press detection.
   */
  onTouchStart(): void {
    this.clearLongPress();
    this.touchMoved = false;

    this.longPressTimeout = setTimeout(() => {
      this.longPressTimeout = null;
      this.showFromTouch();
    }, TOUCH_LONG_PRESS_MS);
  }

  onTouchMove(): void {
    this.touchMoved = true;
    this.clearLongPress();
  }

  onTouchEnd(): void {
    if (this.longPressTimeout) {
      this.clearLongPress();
      if (!this.touchMoved) {
        this.showFromTouch();
      }
    }
  }

  onTouchCancel(): void {
    this.clearLongPress();
  }

  // ---------------------------------------------------------------------------
  // Private helpers
  // ---------------------------------------------------------------------------

  private showFromTouch(): void {
    if (this.tooltipDisabled() || !this.resolveContent()) {
      return;
    }

    if (!this.elementRef.nativeElement.isConnected) {
      return;
    }

    this.touchTriggered = true;
    this.createOverlay();

    this.touchDismissTimeout = setTimeout(() => {
      this.touchDismissTimeout = null;
      this.hideImmediately();
    }, TOUCH_AUTO_DISMISS_MS);

    this.addOutsideTapListener();
  }

  private clearLongPress(): void {
    if (this.longPressTimeout) {
      clearTimeout(this.longPressTimeout);
      this.longPressTimeout = null;
    }
  }

  private clearTouchDismissTimeout(): void {
    if (this.touchDismissTimeout) {
      clearTimeout(this.touchDismissTimeout);
      this.touchDismissTimeout = null;
    }
  }

  private addOutsideTapListener(): void {
    this.removeOutsideTapListener();

    const listener = (event: Event) => {
      const target = event.target as HTMLElement | null;
      const isInsideTrigger = this.elementRef.nativeElement.contains(target);
      const isInsideOverlay = this.overlayRef?.overlayElement?.contains(target) ?? false;

      if (!isInsideTrigger && !isInsideOverlay) {
        this.zone.run(() => this.hideImmediately());
      }
    };

    this.outsideTapListener = listener;

    // Use `touchstart` on document so the very next tap dismisses the tooltip.
    // Registered outside Angular zone to avoid unnecessary change detection on
    // every touch event across the document.
    this.zone.runOutsideAngular(() => {
      document.addEventListener('touchstart', listener, { passive: true });
    });
  }

  private removeOutsideTapListener(): void {
    if (this.outsideTapListener) {
      document.removeEventListener('touchstart', this.outsideTapListener);
      this.outsideTapListener = null;
    }
  }

  private createOverlay(): void {
    if (this.overlayRef) {
      return;
    }

    const positionStrategy = this.positionBuilder.flexibleConnectedTo(this.elementRef).withPositions(this.getPositions()).withPush(true);

    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.close(),
      panelClass: 'zag-tooltip-panel',
    });

    const portal = new ComponentPortal(TooltipComponent);
    this.tooltipRef = this.overlayRef.attach(portal);

    this.panelHovered.set(false);
    this.applyContentToOverlay();
    this.attachOverlayHoverBridge();
  }

  /**
   * Push the currently-resolved content (programmatic or input) into the
   * already-attached overlay component.
   */
  private applyContentToOverlay(): void {
    const instance = this.tooltipRef?.instance;
    if (!instance) return;

    const content = this.resolveContent();
    if (typeof content === 'string') {
      instance.text.set(content);
      instance.templateRef.set(null);
    } else if (content) {
      instance.text.set('');
      instance.templateRef.set(content);
    } else {
      instance.text.set('');
      instance.templateRef.set(null);
    }
    instance.position.set(this.tooltipPosition());
  }

  /**
   * Bridge hover between the trigger and the overlay panel so the user can move
   * their cursor onto the tooltip (e.g. to click a link inside it) without the
   * 50 ms hide timer dismissing it.
   */
  private attachOverlayHoverBridge(): void {
    if (!this.overlayRef) return;

    const panel = this.overlayRef.overlayElement;

    const onEnter = (): void => {
      this.panelHovered.set(true);
      if (this.hideTimeout) {
        clearTimeout(this.hideTimeout);
        this.hideTimeout = null;
      }
    };

    const onLeave = (): void => {
      this.panelHovered.set(false);
      this.hide();
    };

    this.overlayMouseEnterListener = onEnter;
    this.overlayMouseLeaveListener = onLeave;

    this.zone.runOutsideAngular(() => {
      panel.addEventListener('mouseenter', onEnter);
      panel.addEventListener('mouseleave', onLeave);
    });
  }

  private detachOverlayHoverBridge(): void {
    if (!this.overlayRef) return;
    const panel = this.overlayRef.overlayElement;
    if (this.overlayMouseEnterListener) {
      panel.removeEventListener('mouseenter', this.overlayMouseEnterListener);
      this.overlayMouseEnterListener = null;
    }
    if (this.overlayMouseLeaveListener) {
      panel.removeEventListener('mouseleave', this.overlayMouseLeaveListener);
      this.overlayMouseLeaveListener = null;
    }
  }

  private resolveContent(): string | TemplateRef<unknown> | null {
    return this.programmaticContent() ?? this.zagTooltip();
  }

  private destroyOverlay(): void {
    if (this.overlayRef) {
      this.detachOverlayHoverBridge();
      this.overlayRef.dispose();
      this.overlayRef = null;
      this.tooltipRef = null;
      this.panelHovered.set(false);
    }
  }

  private getPositions(): ConnectedPosition[] {
    const position = this.tooltipPosition();

    const positions: Record<TooltipPosition, ConnectedPosition[]> = {
      above: [
        { originX: 'center', originY: 'top', overlayX: 'center', overlayY: 'bottom', offsetY: -4 },
        { originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top', offsetY: 4 },
      ],
      below: [
        { originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top', offsetY: 4 },
        { originX: 'center', originY: 'top', overlayX: 'center', overlayY: 'bottom', offsetY: -4 },
      ],
      left: [
        { originX: 'start', originY: 'center', overlayX: 'end', overlayY: 'center', offsetX: -4 },
        { originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center', offsetX: 4 },
      ],
      right: [
        { originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center', offsetX: 4 },
        { originX: 'start', originY: 'center', overlayX: 'end', overlayY: 'center', offsetX: -4 },
      ],
    };

    return positions[position];
  }
}
